import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Link,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Container
} from '@mui/material';
import { Eye, CreditCard, X, Trash } from '@phosphor-icons/react';
import api from '../../utils/axiosConfig';
import AIUsageModal from './AIUsageModal';
import { supplierCategories } from '../../config/supplierTaxonomy';

// Import Tab Components
import Profile from './SupplierTabs/Profile';
import Messages from './SupplierTabs/Messages';
import Bookings from './SupplierTabs/Bookings';
import Staff from './SupplierTabs/Staff';
import Referral from './SupplierTabs/Referral';

const titleCase = (value) => value
  .split(' ')
  .filter(Boolean)
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

const prettifyValue = (value) => {
  if (!value) return '—';
  return titleCase(String(value).replace(/[_-]+/g, ' '));
};

const categoryLabelMap = Object.fromEntries(
  supplierCategories.map(category => [category.value, category.label])
);

const planDisplayName = (key) => {
  const map = {
    supplier_free: 'Free',
    supplier_free_trial: 'Free Trial',
    supplier_individual: 'Individual',
    supplier_team: 'Team',
    supplier_enterprise: 'Enterprise'
  };
  return map[key] || prettifyValue(key);
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB');
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatMoney = (value) => {
  if (typeof value !== 'number') return '—';
  return `£${value.toFixed(2)}`;
};

const getSupplierStatus = (supplier) => {
  if (!supplier.isVerified) {
    return { label: 'Not Verified', color: 'default' };
  }
  if (supplier.status === 'active') {
    return { label: 'Active', color: 'success' };
  }
  if (supplier.status === 'pending') {
    return { label: 'Pending', color: 'warning' };
  }
  return { label: prettifyValue(supplier.status), color: 'warning' };
};

const getPlanMetaText = (supplier) => {
  if (supplier.plan === 'supplier_free_trial') {
    return `Trial expires ${formatDate(supplier.trialEndsAt)}`;
  }
  if (supplier.contractEndDate) {
    return `Contract ends ${formatDate(supplier.contractEndDate)}`;
  }
  return '';
};

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && (
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

const Suppliers = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [aiUsageModalOpen, setAiUsageModalOpen] = useState(false);
  const [selectedSupplierForAI, setSelectedSupplierForAI] = useState(null);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  useEffect(() => {
    fetchSuppliers();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [page, rowsPerPage]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/suppliers', {
        params: {
          page: page + 1,
          limit: rowsPerPage
        }
      });
      // Handle both old format (array) and new format (object with suppliers array)
      const supplierList = Array.isArray(response.data) ? response.data : (response.data.suppliers || []);
      if (Array.isArray(response.data)) {
        setSuppliers(response.data);
        setTotalCount(response.data.length);
      } else {
        setSuppliers(response.data.suppliers || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
      // Refresh selectedSupplier so the open dialog shows updated data (e.g. after reassign)
      setSelectedSupplier((prev) => {
        if (!prev) return prev;
        const updated = supplierList.find((s) => s._id === prev._id);
        return updated || prev;
      });
    } catch (err) {
      setError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setDialogOpen(true);
    setCurrentTab(0);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSupplier(null);
    setCurrentTab(0);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleSupplierUpdate = () => {
    fetchSuppliers();
  };

  const handleDeleteClick = (supplier, e) => {
    e?.stopPropagation?.();
    setSupplierToDelete(supplier);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!supplierToDelete) return;
    try {
      setError('');
      await api.delete(`/api/admin/supplier/${supplierToDelete._id}`);
      setDeleteConfirmOpen(false);
      setSupplierToDelete(null);
      if (selectedSupplier?._id === supplierToDelete._id) {
        handleCloseDialog();
      }
      fetchSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete business.');
    }
  };

  const handleViewFullProfile = () => {
    if (!selectedSupplier?._id) return;
    navigate(`/admin/suppliers/${selectedSupplier._id}/profile`);
  };

  const handleViewSubscription = async (supplier) => {
    setSubscriptionError('');
    setSelectedSubscription(null);
    setSubscriptionDialogOpen(true);

    if (!supplier.subscriptionId) {
      setSubscriptionError('No subscription record found for this supplier yet.');
      return;
    }

    setSubscriptionLoading(true);
    try {
      const response = await api.get(`/api/admin/business-subscriptions/${supplier.subscriptionId}`);
      setSelectedSubscription(response.data);
    } catch (err) {
      setSubscriptionError(err.response?.data?.message || 'Failed to load subscription details.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  if (loading && page === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Suppliers Management
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Business Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>User Account</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Verification Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map((supplier) => (
                <TableRow key={supplier._id}>
                  {(() => {
                    const status = getSupplierStatus(supplier);
                    return (
                      <>
                  <TableCell>{supplier.businessName}</TableCell>
                  <TableCell>{categoryLabelMap[supplier.category] || prettifyValue(supplier.category)}</TableCell>
                  <TableCell>
                    {supplier.user?._id ? (
                      <Box>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => navigate(`/admin/users#user-${supplier.user._id}`)}
                        >
                          {supplier.user.email}
                        </Link>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Last logged in at {formatDateTime(supplier.user.lastLogin)}
                        </Typography>
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{planDisplayName(supplier.plan)}</Typography>
                    {getPlanMetaText(supplier) && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {getPlanMetaText(supplier)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewSupplier(supplier)}
                      title="View Supplier Details"
                    >
                      <Eye size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewSubscription(supplier)}
                      title="View subscription details"
                      color="primary"
                    >
                      <CreditCard size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteClick(supplier, e)}
                      title="Delete business"
                      color="error"
                    >
                      <Trash size={18} />
                    </IconButton>
                  </TableCell>
                      </>
                    );
                  })()}
                </TableRow>
              ))}
              {suppliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No suppliers found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Supplier Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedSupplier && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  {selectedSupplier.businessName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedSupplier.user && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSelectedSupplierForAI(selectedSupplier.user);
                        setAiUsageModalOpen(true);
                      }}
                    >
                      AI usage
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleViewFullProfile}
                  >
                    View profile
                  </Button>
                  <IconButton onClick={handleCloseDialog} size="small">
                    <X size={18} />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              <Box>
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Profile" />
                  <Tab label="Messages" />
                  <Tab label="Availability" />
                  <Tab label="Bookings" />
                  <Tab label="Referral" />
                </Tabs>

                <TabPanel value={currentTab} index={0}>
                  <Profile supplier={selectedSupplier} onUpdate={handleSupplierUpdate} />
                </TabPanel>
                <TabPanel value={currentTab} index={1}>
                  <Messages supplier={selectedSupplier} />
                </TabPanel>
                <TabPanel value={currentTab} index={2}>
                  <Staff supplier={selectedSupplier} />
                </TabPanel>
                <TabPanel value={currentTab} index={3}>
                  <Bookings supplier={selectedSupplier} />
                </TabPanel>
                <TabPanel value={currentTab} index={4}>
                  <Referral supplier={selectedSupplier} />
                </TabPanel>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Subscription details</DialogTitle>
        <DialogContent dividers>
          {subscriptionLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : subscriptionError ? (
            <Alert severity="info">{subscriptionError}</Alert>
          ) : selectedSubscription ? (
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              <Typography variant="body2"><strong>Plan:</strong> {planDisplayName(selectedSubscription.currentPlanMembershipLevelKey)}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {selectedSubscription.status || '—'}</Typography>
              <Typography variant="body2"><strong>Monthly amount:</strong> {formatMoney(selectedSubscription.totalMonthlyAmount)}</Typography>
              <Typography variant="body2"><strong>Billing period:</strong> {formatDate(selectedSubscription.billingPeriodStart)} to {formatDate(selectedSubscription.billingPeriodEnd)}</Typography>
              <Typography variant="body2"><strong>Next billing date:</strong> {formatDate(selectedSubscription.nextBillingDate)}</Typography>
              <Typography variant="body2"><strong>Contract:</strong> {formatDate(selectedSubscription.contractStartDate)} to {formatDate(selectedSubscription.contractEndDate)}</Typography>
              <Typography variant="body2"><strong>Auto renew:</strong> {selectedSubscription.autoRenew ? 'Yes' : 'No'}</Typography>
              <Typography variant="body2"><strong>Trial ends:</strong> {formatDate(selectedSubscription.trialEndsAt)}</Typography>
              <Typography variant="body2"><strong>Additional users:</strong> {selectedSubscription.additionalUsers?.count ?? 0}</Typography>
              <Box sx={{ pt: 0.5 }}>
                <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Add-ons</strong></Typography>
                {(selectedSubscription.addons || []).filter(addon => addon.isActive).length ? (
                  (selectedSubscription.addons || [])
                    .filter(addon => addon.isActive)
                    .map((addon) => (
                      <Chip
                        key={`${addon.addonKey}-${addon.addedAt || ''}`}
                        label={`${addon.addonKey} x${addon.quantity || 1} (${formatMoney((addon.price || 0) * (addon.quantity || 1))})`}
                        size="small"
                        sx={{ mr: 0.75, mb: 0.75 }}
                      />
                    ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No active add-ons</Typography>
                )}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete business confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete business</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{supplierToDelete?.businessName}</strong>? This will remove the business and all memberships. User accounts are preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Usage Modal */}
      <AIUsageModal
        open={aiUsageModalOpen}
        onClose={() => {
          setAiUsageModalOpen(false);
          setSelectedSupplierForAI(null);
        }}
        userEmail={selectedSupplierForAI?.email}
        userId={selectedSupplierForAI?._id}
      />
    </Container>
  );
};

export default Suppliers; 